import { z } from 'zod';
import { cursorPageQuerySchema } from './pagination.js';
/**
 * Billing.
 *
 * RevenueCat is the source of truth; `Subscription` is a read model kept in
 * step by its webhook, and `SubscriptionEvent` is the append-only log of
 * deliveries that got us here. The console reads both and can re-apply one
 * event — which is the whole of what "fixing billing" means when the read model
 * and RevenueCat disagree.
 */
export const SUBSCRIPTION_STATUSES = ['active', 'trialing', 'canceled', 'expired', 'billing_issue'];
export const SUBSCRIPTION_STORES = ['app_store', 'play_store', 'promotional'];
export const adminSubscriptionSchema = z.object({
    userId: z.string().uuid(),
    displayName: z.string().nullable(),
    phone: z.string(),
    status: z.enum(SUBSCRIPTION_STATUSES),
    /**
     * What the user can actually do right now, which is not the same as the
     * status: a cancellation keeps Premium until the period ends. Showing only
     * the status is how support tells someone they have lost access they still
     * have.
     */
    entitlement: z.enum(['free', 'premium']),
    store: z.enum(SUBSCRIPTION_STORES).nullable(),
    productId: z.string().nullable(),
    willRenew: z.boolean(),
    currentPeriodEnd: z.string().nullable(),
    trialEndsAt: z.string().nullable(),
    updatedAt: z.string(),
    createdAt: z.string(),
});
export const adminSubscriptionListQuerySchema = cursorPageQuerySchema.extend({
    /** Matches display name or phone, like the users list. */
    search: z.string().trim().max(120).optional(),
    status: z.enum(SUBSCRIPTION_STATUSES).optional(),
    store: z.enum(SUBSCRIPTION_STORES).optional(),
    entitlement: z.enum(['free', 'premium']).optional(),
});
// --- Webhook events ---------------------------------------------------------
export const adminBillingEventSchema = z.object({
    id: z.string().uuid(),
    /** RevenueCat's own event id — the key that makes a redelivery a no-op. */
    revenueCatEventId: z.string(),
    /**
     * Null when the delivery named an `app_user_id` that is not one of ours —
     * RevenueCat's anonymous pre-login ids, or a sandbox event for an account
     * that has since gone. Those are stored rather than dropped, precisely so
     * this screen can show that they arrived.
     */
    userId: z.string().uuid().nullable(),
    displayName: z.string().nullable(),
    type: z.string(),
    /**
     * Whether re-applying this delivery could change anything, and why not when
     * it cannot.
     *
     * On the list item rather than only on the detail: the table offers a
     * per-row Re-apply, and deciding whether to enable it from the detail
     * endpoint would mean one request per visible row.
     */
    replayable: z.boolean(),
    replayBlockedReason: z.string().nullable(),
    createdAt: z.string(),
});
export const adminBillingEventDetailSchema = adminBillingEventSchema.extend({
    /** The delivery exactly as RevenueCat sent it. Opaque: their shape, not ours. */
    payload: z.unknown(),
});
export const adminBillingEventListQuerySchema = cursorPageQuerySchema.extend({
    type: z.string().trim().max(64).optional(),
    userId: z.string().uuid().optional(),
    /**
     * Matches RevenueCat's own event id, which is what an operator has when they
     * arrive from RevenueCat's dashboard or a support thread.
     *
     * Deliberately not a subscriber search: `subscription_events` has no
     * relation to `users` — an event whose `app_user_id` was never one of ours is
     * stored anyway, which is the whole reason this list exists — so a name match
     * would mean resolving ids first and silently truncating the set. The
     * subscriptions view already links here with `userId` set, which is the
     * accurate way to ask that question.
     */
    search: z.string().trim().max(128).optional(),
});
export const adminBillingEventParamsSchema = z.object({
    id: z.string().uuid(),
});
/**
 * What a replay did.
 *
 * Re-applying a stored delivery is deliberately not idempotent-guarded: the
 * guard exists to stop RevenueCat's own retries double-applying, and an
 * operator asking for a replay is asking to bypass exactly that. The result
 * says what the subscription looks like afterwards so the effect is visible
 * rather than assumed.
 */
export const adminBillingReplayResultSchema = z.object({
    eventId: z.string().uuid(),
    applied: z.boolean(),
    /** Present when `applied` is false — nothing changed, and this is why. */
    skippedReason: z.string().nullable(),
    subscription: adminSubscriptionSchema.nullable(),
});
//# sourceMappingURL=billing.js.map